/**
 * Hooks for the STUDENT SURFACE (lanes 1C/1D): their classroom and their record.
 *
 * They open no new data source — they compose the hooks that already exist
 * (`use-assessment`, `use-schedule-data`, `use-session-plan`, `use-people-data`)
 * with the pure selectors from `@space/shared`. Everything they read already
 * travels to the student's device via `my_cohorts_student`, so the
 * classroom and the report card work the same offline and no new sync rules
 * are needed.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  attendanceCertification,
  type AttendanceCertification,
  attendanceStatsOf,
  attendanceTimeline,
  type AttendanceTimelineRow,
  buildReportCard,
  type CohortProgressSummary,
  cohortSessionsProgress,
  type CriterionProgressRow,
  directoryRowTitle,
  isPlanPhase,
  type PlanCompletionCtx,
  type PlanModule,
  type ReportCard,
  type ReportCardGrade,
} from "@space/shared";
import { useSession } from "@/lib/auth";
import { useGradeCategories, useGradeItems, useMyGrades } from "./use-assessment";
import { useUserEnrollments } from "./use-people-data";
import { useCohortSessions, useMyAttendance } from "./use-schedule-data";
import {
  useCohortPlanItems,
  useCourseModuleDirectory,
  useMyCourseProgress,
  useMySubmissions,
  useMyWatchProgress,
} from "./use-session-plan";

/** MY enrollment in a cohort (the own row that syncs). */
export function useMyEnrollment(cohortId: Accessor<string | undefined>) {
  const { userId } = useSession();
  const enrollments = useUserEnrollments(userId);
  const row = createMemo(() => (enrollments.data ?? []).find((e) => e.cohortId === cohortId()));
  const stats = createMemo(() =>
    attendanceStatsOf(row()?.raw as Record<string, unknown> | undefined),
  );
  return {
    row,
    /** Attendance stats denormalized by the server (rate in 0–100). */
    stats,
    isLoading: () => enrollments.isLoading,
  };
}

/**
 * MY attendance history in a cohort + the percentage against the
 * certification criterion. The percentage is NOT recomputed here: it comes
 * from the criterion the server evaluates or, if the cohort has no rules,
 * from the stats the server itself denormalizes onto the enrollment.
 */
export function useMyAttendanceRecord(
  cohortId: Accessor<string | undefined>,
  criteria: Accessor<readonly CriterionProgressRow[]>,
): {
  timeline: Accessor<AttendanceTimelineRow[]>;
  certification: Accessor<AttendanceCertification>;
} {
  const sessions = useCohortSessions(cohortId);
  const records = useMyAttendance();
  const enrollment = useMyEnrollment(cohortId);

  const mine = createMemo(() => (records.data ?? []).filter((r) => r.cohort_id === cohortId()));

  const timeline = createMemo(() => attendanceTimeline(mine(), sessions.data ?? []));
  const certification = createMemo(() => attendanceCertification(enrollment.stats(), criteria()));
  return { timeline, certification };
}

/** A cohort's plan items grouped by class + tick context. */
function useCohortPlan(
  cohortId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
) {
  const planItems = useCohortPlanItems(cohortId);
  const directory = useCourseModuleDirectory(courseId);
  const progress = useMyCourseProgress(courseId);
  const submissions = useMySubmissions();
  const watch = useMyWatchProgress();

  const dirMap = createMemo(() => {
    const map = new Map<
      string,
      { kind: string | null; ref_id: string | null; title: string | null }
    >();
    for (const r of directory.data ?? []) {
      map.set(r.id, { kind: r.kind, ref_id: r.ref_id, title: directoryRowTitle(r) });
    }
    return map;
  });

  const bySession = createMemo(() => {
    const dir = dirMap();
    const out = new Map<string, PlanModule[]>();
    for (const sm of planItems.data ?? []) {
      const sessionId = sm.class_session_id;
      if (!sessionId) {
        continue;
      }
      const d = dir.get(sm.module_id ?? "");
      const list = out.get(sessionId) ?? [];
      list.push({
        id: sm.id,
        moduleId: sm.module_id ?? "",
        phase: isPlanPhase(sm.phase) ? sm.phase : "pre",
        position: sm.position ?? 0,
        kind: d?.kind ?? null,
        refId: d?.ref_id ?? null,
        title: d?.title ?? null,
      });
      out.set(sessionId, list);
    }
    return out;
  });

  const ctx = createMemo<PlanCompletionCtx>(() => {
    const lessonStatusByRef = new Map<string, string | null>();
    for (const p of progress.data ?? []) {
      if (p.lesson_id) {
        lessonStatusByRef.set(p.lesson_id, p.status);
      }
    }
    const submittedByAssignment = new Map<string, boolean>();
    for (const s of submissions.data ?? []) {
      if (s.assignment_id) {
        submittedByAssignment.set(s.assignment_id, true);
      }
    }
    const videoDoneByRef = new Map<string, boolean>();
    for (const w of watch.data ?? []) {
      if (w.video_id) {
        videoDoneByRef.set(w.video_id, Boolean(w.completed_at));
      }
    }
    return { lessonStatusByRef, submittedByAssignment, videoDoneByRef };
  });

  return { bySession, ctx, lessonProgress: progress };
}

/**
 * "My N classes" grid for a cohort (D1): per class, whether I attended and
 * how much of the plan I've done. Per-class counting is done by
 * `planProgress` (session-plan) — the same function that renders the ticks
 * in the class plan view.
 */
export function useMyCohortProgress(
  cohortId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
): Accessor<CohortProgressSummary> {
  const sessions = useCohortSessions(cohortId);
  const records = useMyAttendance();
  const plan = useCohortPlan(cohortId, courseId);

  const summary = createMemo(() =>
    cohortSessionsProgress(
      sessions.data ?? [],
      plan.bySession(),
      plan.ctx(),
      (records.data ?? []).filter((r) => r.cohort_id === cohortId()),
    ),
  );
  return summary;
}

/**
 * MY report card for a cohort (E2): categories, weights, my grades, the
 * official final and the projected one. `grade_category`/`grade_item` sync
 * to the whole cohort (weight transparency) but `assignment_grade` only
 * brings MY rows, so nobody else's grade can come out of here.
 */
export function useMyReportCard(
  cohortId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
): Accessor<ReportCard> {
  const { userId } = useSession();
  const categories = useGradeCategories(cohortId);
  const items = useGradeItems(cohortId);
  const grades = useMyGrades(userId);
  const enrollment = useMyEnrollment(cohortId);
  const lessonProgress = useMyCourseProgress(courseId);

  const gradeByAssignment = createMemo(() => {
    const map = new Map<string, ReportCardGrade>();
    for (const g of grades.data ?? []) {
      if (g.assignment_id && g.cohort_id === cohortId()) {
        map.set(g.assignment_id, { score: g.score ?? null, status: g.status ?? null });
      }
    }
    return map;
  });

  // Average of already-completed lessons (same source as `auto_blocks` on
  // the server); `null` while there are none, so the item counts as
  // PENDING in the projection instead of as a zero.
  const autoBlocksFraction = createMemo(() => {
    const scores = (lessonProgress.data ?? [])
      .filter((p) => p.status === "completed")
      .map((p) => p.score ?? 0);
    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  });

  const attendanceFraction = createMemo(() => {
    const { rate } = enrollment.stats();
    return rate === null ? null : rate / 100;
  });

  const card = createMemo(() =>
    buildReportCard(categories.data ?? [], items.data ?? [], {
      gradeByAssignment: gradeByAssignment(),
      autoBlocksFraction: autoBlocksFraction(),
      attendanceFraction: attendanceFraction(),
    }),
  );
  return card;
}
